import _ from 'underscore';
import React, { useMemo, useContext, useCallback, useState, useEffect } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { generateGuid } from '@bingads-webui-universal/primitive-utilities';
import { getLocalizedSupportLink } from '@bingads-webui-campaign/overview-cards-common';
import { getScope } from '@bingads-webui-campaign/fluent-page-data';
import {
  ADINSIGHT_LOG_API_NAME,
  RECOMMENDATION_IDS,
  getChannel,
} from '@bingads-webui-campaign/recommendation-core';
import { withDelayedStyles } from '@bingads-webui-campaign-react/common-utils';
import { getTipCardFluentV2Style } from '@bingads-webui-campaign-react/overview-card-styles';

import { IOverviewRecommendationsCardProps, IScopeContext } from './types';
import { useRecommendationsData } from './use-recommendations-data';
import { CardContent } from './components/card-content';
import { instrumentationSchema, logApi, numberOfTipsAndRecommendations } from './const';
import { logAdInsightOppTracking, logSlideDisplay as log } from './card-data';
import { ShimmerCard } from './components/shimmer-card';

const getDefaultStyles = (_, { palette = {} }) => ({
  '@global': Object.assign(getTipCardFluentV2Style(palette)),
});

export function OverviewRecommendationsCardInternal({
  i18n,
  perfMarkerUtils,
  navigationLevel,
  campaignType,
  serverContext,
  permissions,
  legacyConfig,
  jsdata,
  featureInstrumentation,
  instrumentationConstants,
  instrumentationLegacy,
  constructOptions,
  i18nErrors,
  preferencesService,
  locationMap,
  legacyRouter,
  walkme,
  adDisplayUrlService,
}: IOverviewRecommendationsCardProps) {
  const isMCC = navigationLevel === 'customer';
  const {
    entitySet,
    navigationLevel: navigationLevelOption,
  } = constructOptions({
    navigationLevel,
    campaignType,
  });
  const [activeItemsInternal, setActiveItemsInternal] = useState([]);
  const [allItemsInternal, setAllItemsInternal] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { scope } = useContext(ScopeContext) as IScopeContext;
  const { aid, cid, campaignId } = scope?.values;
  const guid = useMemo(() => generateGuid(), []);

  // we have internal state to allow user to dismiss tips
  // so when we reload data due to filter change, we need to reset the state
  const onSuccess = useCallback(() => {
    setActiveItemsInternal([]);
    setAllItemsInternal([]);
    setCurrentSlide(0);
  }, []);
  const { data, isLoading } = useRecommendationsData({
    entitySet,
    navigationLevel: navigationLevelOption,
    featureInstrumentation,
    legacyConfig,
    campaignType,
    serverContext,
    jsdata,
    permissions,
    cid,
    aid,
    campaignId,
    instrumentationLegacy,
    instrumentationConstants,
    isMCC,
    guid,
    willFetchData: perfMarkerUtils.willFetchData,
    onSuccess,
  });
  const { activeItems, allItems } = data || { activeItems: [], allItems: [] };
  const { consultationLink } = getLocalizedSupportLink({
    Culture: serverContext.CurrentLocale,
  });
  const currentActivity = useMemo(() => {
    featureInstrumentation.addScenario(instrumentationSchema);
    const currentScenario = featureInstrumentation.loadRecommendationOverviewTile.create();
    return currentScenario.loadRecommendationOverviewTile.create();
  }, [featureInstrumentation]);
  const recommendationScope = getScope({
    navigationLevel: navigationLevelOption,
    legacyConfig,
    cid,
    aid,
    campaignId,
  });
  const viewRecommendation = useCallback((opportunityType, category) => () => {
    if (isMCC) {
      legacyConfig.set('recsrc', 'bingadsweb[mccoverviewpage]');
    } else {
      legacyConfig.set('recsrc', 'Opportunities[Overview]');
    }

    instrumentationLegacy.logMessage([instrumentationConstants.LOGCATEGORY.TRACE], instrumentationConstants.LOGLEVEL.INFO, logApi, `Opportunity clicked. Opportunity Id: ${opportunityType}`);
    logAdInsightOppTracking({
      oppt: opportunityType === 'All' ? { opportunityType: 'All' } : activeItemsInternal[currentSlide],
      isClick: true,
      position: currentSlide + 1,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
      isMCC,
    });

    if (navigationLevelOption === 'account') {
      legacyRouter.navigateToRoute('recommendation_details', _.extend({
        type: opportunityType,
      }, category ? { category } : undefined));
    } else if (navigationLevelOption === 'customer') {
      legacyRouter.navigateToRoute('mcc_recommendation_details', _.extend({
        type: opportunityType,
      }, category ? { category } : undefined));
    } else {
      legacyRouter.navigateToRoute('campaign_recommendation_details', _.extend({
        campaign_id: campaignId,
        type: opportunityType,
      }, category ? { category } : undefined));
    }
  }, [activeItemsInternal, campaignId, currentSlide, guid, instrumentationConstants,
    instrumentationLegacy, isMCC, legacyConfig, legacyRouter, navigationLevelOption]);

  const logOpportunityEvent = useCallback(({
    type = null,
    action,
    position = null,
    input = null,
    context = null,
  }) => {
    const api = ADINSIGHT_LOG_API_NAME.RECOMMENDATION;

    currentActivity.trace({
      type: type || '',
      typeId: RECOMMENDATION_IDS[type],
      action,
      input,
      campaignId: recommendationScope.campaignId,
      channel: getChannel(type, false, isMCC),
      position,
      guid,
      context: JSON.stringify(context),
    }, api);
  }, [currentActivity, guid, isMCC, recommendationScope.campaignId]);
  const logSlideDisplay = useCallback((reason) => {
    log({
      reason,
      instrumentationLegacy,
      instrumentationConstants,
      activeItems: activeItemsInternal,
      currentSlide,
      campaignId,
      guid,
      isMCC,
    });
  }, [activeItemsInternal, campaignId, currentSlide, guid, instrumentationConstants, instrumentationLegacy, isMCC]);

  useEffect(() => {
    if (!isLoading && _.isEmpty(activeItemsInternal)) {
      perfMarkerUtils.willRender();
      setActiveItemsInternal(activeItems);
      setAllItemsInternal(allItems);
      perfMarkerUtils.done();
    }
  }, [activeItems, activeItemsInternal, allItems, isLoading, perfMarkerUtils]);

  const removeTipLocally = useCallback((tipId) => {
    const index = _.findIndex(allItemsInternal, tip => tip.tipId === tipId);

    let newAllItemsInternal = allItemsInternal;
    if (index >= 0 && index < allItemsInternal.length) {
      newAllItemsInternal = [
        ...allItemsInternal.slice(0, index),
        ...allItemsInternal.slice(index + 1),
      ];
      setAllItemsInternal(newAllItemsInternal);
    } else {
      instrumentationLegacy.logMessage(
        [instrumentationConstants.LOGCATEGORY.ERROR],
        instrumentationConstants.LOGLEVEL.INFO,
        logApi,
        `Dismiss tip not found. Tip Id: ${tipId}`
      );
    }
    const newActiveItemsInternal = _.first(newAllItemsInternal, numberOfTipsAndRecommendations);
    setActiveItemsInternal(newActiveItemsInternal);
    if (currentSlide >= newActiveItemsInternal.length) {
      setCurrentSlide(newActiveItemsInternal.length - 1);
    }
  }, [allItemsInternal, currentSlide, instrumentationConstants, instrumentationLegacy]);

  if (isLoading) {
    return <ShimmerCard />;
  }

  return (
    <div className="tips-card">
      <CardContent
        activeItems={activeItemsInternal || []}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        i18n={i18n}
        consultationLink={consultationLink}
        i18nErrors={i18nErrors}
        serverContext={serverContext}
        permissions={permissions}
        scope={recommendationScope}
        isMCC={isMCC}
        currentActivity={currentActivity}
        logOpportunityEvent={logOpportunityEvent}
        preferencesService={preferencesService}
        locationMap={locationMap}
        overallOptimizationScoreBar={data?.overallOptimizationScoreBar}
        viewRecommendation={viewRecommendation}
        legacyConfig={legacyConfig}
        walkme={walkme}
        jsdata={jsdata}
        instrumentationConstants={instrumentationConstants}
        instrumentationLegacy={instrumentationLegacy}
        logSlideDisplay={logSlideDisplay}
        adDisplayUrlService={adDisplayUrlService}
        removeTipLocally={removeTipLocally}
      />
    </div>
  );
}

export const OverviewRecommendationsCard = withDelayedStyles(OverviewRecommendationsCardInternal, getDefaultStyles);
